import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Button, Container, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage } from "../../../locales/translationUtils";
import { useWarehouseClient } from "../../../apis/warehouseClient/WarehouseClientProvider";
import { WarehouseClientApiResponse, WarehouseClientPutVM } from "../../../services/warehouseClientService";
import { createWarehouseClientUser, deleteWarehouseClientUser } from "../../../services/warehouseClientUserService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { useGeneric } from '../../../apis/generic/GenericProvider';
import { useUser } from "../../../apis/user/UserProvider";
import User from "../../../types/user";

type Option = {
    id: number;
    name: string;
};

const ViewWarehouseClientById: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { fetchWarehouseClientById, putWarehouseClient, excluiWarehouseClient } = useWarehouseClient();
    const [warehouseClient, setWarehouseClient] = useState<WarehouseClientApiResponse | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { distinctOptions, fetchDistinctOptions } = useGeneric();
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const { fetchUsuarios } = useUser();
    const [usuarios, setUsuarios] = useState<User[]>([]);
    const [clientsList, setClientsList] = useState<Option[]>([]);
    const [warehousesList, setWarehouseList] = useState<Option[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);


    const descriptionRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const usuariosCarregados = await fetchUsuarios();
            setUsuarios(usuariosCarregados);
        };

        fetchData();
    }, []);

    const navItems: NavBarItem[] = [
        { label: t('warehouseClient.labels.home'), route: '/', iconName: 'house-door' },
        { label: t('warehouseClient.labels.list'), route: '/warehouseclient', iconName: 'table' },
        { label: t('warehouseClient.labels.new'), route: '/warehouseclient/create', iconName: 'plus-circle' },
    ];

    useEffect(() => {
        if (isEditing && descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const loadWarehouseClient = async () => {
            try {
                setLoading(true);
                setError(false);
                if (!id) throw new Error("ID inválido");

                const result = await fetchWarehouseClientById(Number(id));

                setWarehouseClient(result);

            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        loadWarehouseClient();
    }, [id, fetchWarehouseClientById]);


    useEffect(() => {
        if (isEditing) {
            (async () => {
                fetchDistinctOptions("wareHouse");
                fetchDistinctOptions("client");

                setWarehouseList(
                    (distinctOptions["wareHouse"] || []).map(item => ({
                        id: item.id,
                        name: item.description || ""
                    }))
                );

                setClientsList(
                    (distinctOptions["client"] || []).map(item => ({
                        id: item.id,
                        name: item.description || ""
                    }))
                );
            })();
        }
    }, [isEditing]);

    useEffect(() => {
        if (warehouseClient?.data?.[0]) {
            const clientId = warehouseClient.data[0].clientId;
            const wrId = warehouseClient.data[0].wrId;
            const userIds = warehouseClient.data[0].users?.map(u => u.id) || [];

            setSelectedClientId(clientId);
            setSelectedWarehouseId(wrId);
            setSelectedUserIds(userIds);
        }
    }, [warehouseClient]);


    const preparePayload = (): WarehouseClientPutVM | null => {
        if (!warehouseClient) return null;

        return {
            id: warehouseClient.data[0].id,
            clientId: selectedClientId,
            wrId: selectedWarehouseId,
            usersIds: selectedUserIds,
        };
    };

    const warehouseClientRef = useRef(warehouseClient);

    const handleEdit = async () => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        try {
            if (!warehouseClient) return;

            const payload = preparePayload();
            if (!payload) return;

            const response = await putWarehouseClient(warehouseClient.data[0].id, payload);

            await deleteWarehouseClientUser(warehouseClient.data[0].id);

            await Promise.all(
                payload.usersIds.map(userId =>
                    createWarehouseClientUser({
                        userId,
                        warehouseClientId: warehouseClient.data[0].id
                    })
                )
            );

            setWarehouseClient(prev =>
                prev
                    ? {
                        ...prev,
                        data: {
                            ...prev.data,
                            client: clientsList.find(client => client.id === payload.clientId),
                            wr: warehousesList.find(warehouse => warehouse.id === payload.wrId),
                            users: payload.usersIds.map(id => ({
                                id,
                                username: prev.data[0].users?.find(user => user.id === id)?.username || ""
                            }))
                        }
                    }
                    : prev
            );


            warehouseClientRef.current = warehouseClient;

            const updatedData = await fetchWarehouseClientById(warehouseClient.data[0].id);
            if (updatedData) {
                setWarehouseClient(updatedData);
            }

            const successMessage = getSuccessMessage("editWarehouseClient", { id: warehouseClient.data[0].id, });
            navigate(`/WarehouseClient`, {
                state: {
                    message: successMessage,
                    variant: "success",
                },
            });

        } catch (error) {
            console.error("Erro ao atualizar os dados:", error);
            alert("Erro ao atualizar os dados.");
        } finally {
            setIsEditing(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            await excluiWarehouseClient(Number(id));
            
            const successMessage = getSuccessMessage("deleteWarehouseClient", { id: id, });
            navigate(`/WarehouseClient`, {
                state: {
                    message: successMessage,
                    variant: "success",
                },
            });

        } catch (error) {
            alert("Erro ao excluir o registro.");
        }
    };

    const handleNavigation = (route: string) => {
        navigate(route);
    };

    if (loading) return <div className="text-center mt-4">Carregando...</div>;
    if (error || !warehouseClient) return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;

    return (
        <Container fluid>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editWarehouseClient") : getPageName("viewWarehouseClient")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Form.Group className="mb-2"></Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseClient.fields.id')}</Form.Label>
                    <Form.Control type="text" value={warehouseClient?.data[0]?.id || ""} readOnly style={{ width: "20%", backgroundColor: "#f0f0f0" }} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseClient.fields.client')}</Form.Label>
                    {isEditing ? (
                        <Form.Select
                            value={selectedClientId ?? ""}
                            onChange={(e) => setSelectedClientId(Number(e.target.value))}
                            disabled={!isEditing}
                            style={{ width: "20%" }}
                        >
                            <option value="">{t('warehouseClient.labels.selectClient')}</option>
                            {distinctOptions['client']?.map(
                                (option: { id: number; description: string }) => (
                                    <option key={option.id} value={option.id.toString()}>
                                        {option.description}
                                    </option>
                                )
                            )}
                        </Form.Select>

                    ) : (
                        <Form.Control
                            type="text"
                            value={warehouseClient.data[0].clientName}
                            readOnly
                            style={{ cursor: "pointer", textDecoration: "underline", width: "20%", backgroundColor: "#f0f0f0" }}
                            onClick={() => handleNavigation(`/client/${warehouseClient.data[0].clientId}`)}
                        />
                    )}
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseClient.fields.wr')}</Form.Label>
                    {isEditing ? (
                        <Form.Select
                            value={selectedWarehouseId ?? ""}
                            onChange={(e) => setSelectedWarehouseId(Number(e.target.value))}
                            disabled={!isEditing}
                            style={{ width: "20%" }}
                        >
                            <option value="">{t('warehouseClient.labels.selectWarehouse')}</option>
                            {distinctOptions['wareHouse']?.map(
                                (option: { id: number; description: string }) => (
                                    <option key={option.id} value={option.id.toString()}>
                                        {option.description}
                                    </option>
                                )
                            )}
                        </Form.Select>
                    ) : (
                        <Form.Control
                            type="text"
                            value={warehouseClient.data[0].wrName}
                            readOnly
                            style={{ cursor: "pointer", textDecoration: "underline", width: "20%", backgroundColor: "#f0f0f0" }}
                            onClick={() => handleNavigation(`/wr/${warehouseClient.data[0].wrId}`)}
                        />
                    )}
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseClient.fields.users')}</Form.Label>
                    {isEditing ? (
                        <Form.Select
                            name="users"
                            multiple
                            style={{ width: "20%", height: "150px" }}
                            value={selectedUserIds.map(String)}
                            onChange={(e) => {
                                const selectedIds = Array.from(e.target.selectedOptions, (option) =>
                                    Number(option.value)
                                );
                                setSelectedUserIds(selectedIds);
                            }}
                        >
                            {usuarios?.map((user) => (
                                <option key={user.id} value={String(user.id)}>
                                    {user.username}
                                </option>

                            ))}
                        </Form.Select>

                    ) : (
                        <div>
                            {warehouseClient?.data[0].users && warehouseClient.data[0].users.length > 0 ? (
                                warehouseClient.data[0].users.map((user) => (
                                    <Form.Control
                                        key={user.id}
                                        type="text"
                                        value={user.username}
                                        readOnly
                                        style={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            width: "20%",
                                            backgroundColor: "#f0f0f0",
                                            marginBottom: "5px",
                                            display: "inline-block",
                                            marginRight: "10px",
                                        }}
                                        onClick={() => handleNavigation(`/user/${user.id}`)}
                                    />
                                ))
                            ) : (
                                <Form.Control
                                    type="text"
                                    value=""
                                    readOnly
                                    style={{
                                        width: "20%",
                                        backgroundColor: "#f0f0f0",
                                        marginBottom: "5px",
                                        display: "inline-block",
                                        marginRight: "10px",
                                    }}
                                />
                            )}
                        </div>
                    )}
                </Form.Group>


            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewWarehouseClientById;
